import LoadingSpinner from '@common/src/components/LoadingSpinner';
import useQueryParams from '@common/src/hooks/useQueryParams';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import { useYellowhiveFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/yellowhiveFlowsConfigureStore';
import { useYellowhiveFlowsProducts } from '@flows/src/hooks/flows-configure/useYellowhiveFlowsProducts';
import { PartyType } from '@parties/src/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router';
import { Card, HStack, Text } from '@wegroup/design-system';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import TableCheckbox from '@parties/src/features/overview/components/table/TableCheckbox';

const FlowsConfigureYellowhiveProducts = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryparams = useQueryParams();
  const partyType = (queryparams.get('type') as PartyType) || 'CUSTOMER';
  const { flowId } = useParams<{ flowId: string }>();

  const { data: flowInfo } = useFlowInfo(flowId || '');
  const isYellowhiveFlow = flowInfo?.sub_type === 'YELLOWHIVE';

  const [error, setError] = useState<string | undefined>(undefined);

  const {
    insurances,
    coverages,
    products: selectedProducts,
    setProducts,
    addProduct,
    removeProduct,
  } = useYellowhiveFlowsConfigureStore();

  // Fetch products using the API
  const {
    data: productsApiData,
    isLoading,
    error: productsError,
  } = useYellowhiveFlowsProducts({
    insurance_codes: insurances.map((insurance) => insurance.code),
    coverage_codes: Object.values(coverages).flat(),
    partyType,
  });
  const productsData = productsApiData?.items || [];

  if (!flowId) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}`,
          search: window.location.search,
        }}
      />
    );
  }

  // This component is only for yellowhive flows
  if (!flowInfo) {
    return <LoadingSpinner />;
  }

  if (!isYellowhiveFlow) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const handlePrevious = () => {
    // For yellowhive flows, go back to the last insurance's coverages
    const lastInsurance = insurances[insurances.length - 1];
    if (lastInsurance) {
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${lastInsurance.code}/coverages`,
        search: window.location.search,
      });
    }
  };

  const handleNext = () => {
    // Validate that we have selections
    if (selectedProducts.length === 0) {
      setError(t('Please select at least one product'));
      return;
    }

    history.push({
      pathname: `${FLOWS_CONFIG_PATH}/${flowId}/finish`,
      search: window.location.search,
    });
  };

  const handleProductSelect = (product: typeof selectedProducts[0]) => {
    const isSelected = selectedProducts.some((p) => p.id === product.id);
    if (isSelected) {
      removeProduct(product);
    } else {
      addProduct(product);
    }
    if (error) setError(undefined);
  };

  // Show error from API if available
  if (productsError) {
    setError(t('Failed to fetch products'));
  }

  // Auto-select all products by default on first load
  const didInitRef = useRef(false);
  useEffect(() => {
    if (
      !didInitRef.current &&
      productsData.length > 0 &&
      selectedProducts.length === 0
    ) {
      setProducts(productsData);
      didInitRef.current = true;
    }
  }, [selectedProducts.length, productsData.length, setProducts]);

  const isAllSelected =
    selectedProducts.length === productsData.length && productsData.length > 0;
  const handleSelectAll = () => setProducts(selectedProducts);
  const handleDeselectAll = () => setProducts([]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('CONVERSATIONS_REPORT-CREATION_PRODUCT-SELECTION')}
      error={error}
    >
      <ConfigureItemsGrid>
        {productsData.length > 1 ? (
          <SelectAllButton
            isAllSelected={isAllSelected}
            onDeselectAll={handleDeselectAll}
            onSelectAll={handleSelectAll}
          />
        ) : null}
        {productsData.map((product) => (
          <Card
            key={product.id}
            as="button"
            p="4"
            borderColor={
              error
                ? 'danger.main'
                : selectedProducts.some((p) => p.id === product.id)
                ? 'primary.500'
                : 'gray.50'
            }
            h="100%"
            onClick={() => handleProductSelect(product)}
            _hover={{
              borderColor: selectedProducts.some((p) => p.id === product.id)
                ? undefined
                : 'gray.100',
              boxShadow: error ? undefined : 'level2',
            }}
            cursor="pointer"
            transition="0.1s"
          >
            <HStack spacing="3" align="start">
              <div style={{ flexGrow: 1 }}>
                <Text fontWeight="medium" mb="1">
                  {product.name}
                </Text>
                <Text fontSize="sm" color="gray.600" mb="2">
                  {product.insurance.name}
                </Text>
              </div>
              <TableCheckbox
                isChecked={selectedProducts.some((p) => p.id === product.id)}
                onChange={() => null}
              />
            </HStack>
          </Card>
        ))}
      </ConfigureItemsGrid>
    </ContentWrapper>
  );
};

export default FlowsConfigureYellowhiveProducts;
